import React, { useEffect, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useHistory } from 'react-router-dom'
import { t } from 'i18next'
import CustomerDisplayBranchSelector from './components/CustomerDisplayBranchSelector'
import CustomerDisplayFileUpload from './components/CustomerDisplayFileUpload'
import CustomerDisplayPreview from './components/CustomerDisplayPreview'
import {
  buildCustomerDisplayPayload,
  getAcceptedTypes,
  getMaxFileSize,
  toDateInputValue,
} from './utils/customerDisplayInformation.utils'
import { fetchCompanies } from 'redux/actions/companyAction'
import MiniLoading from 'Components/MiniLoading'
import FilterSelector from 'Components/FilterSelector'
import CustomSnackbar from 'Components/CustomSnackbar'
import { useDispatch, useSelector } from 'react-redux'
import { getWharehouseInfo } from 'redux/actions/clientsActions'
import {
  addCustomerDisplaySetting,
  modifyCustomerDisplaySetting,
  retrieveCustomerDisplaySetting,
} from 'redux/actions/customerDisplayActions'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { CUSTOMER_DISPLAY_MEDIA_TYPES } from '../SearchCustomerDisplay/constants/customerDisplay.constants'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { uploadFileAction } from 'redux/actions/cotizacionActions'
import ToolTipInformation from 'Components/ToolTipInformation/ToolTipInformation'

function CustomerDisplayInformation({ id, editMode }) {
  const dispatch = useDispatch()
  const history = useHistory()

  const [companies, setCompanies] = useState([])
  const [branchOffices, setBranchOffices] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'warning',
  })

  const [fileName, setFileName] = useState('')
  const [fileUrl, setFileUrl] = useState('')
  const tenant = useSelector(state => state.login.checkCompany?.[0]?.SaaSId)

  const validationSchema = yup.object({
    name: yup
      .string()
      .max(200)
      .required(t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED')),
    mediaType: yup
      .string()
      .required(t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED')),
    displayOrder: yup.number().nullable(),
    companyId: yup
      .string()
      .required(t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED')),
    branchOfficeIds: yup
      .array()
      .min(1, t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED'))
      .required(t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED')),
    startDate: yup
      .string()
      .required(t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED')),
    endDate: yup
      .string()
      .required(t('CUSTOMER_DISPLAY_SETTINGS.FORM.REQUIRED'))
      .test(
        'end-after-start',
        t('CUSTOMER_DISPLAY_SETTINGS.FORM.END_DATE_AFTER_START'),
        function (endDate) {
          const { startDate } = this.parent
          if (!startDate || !endDate) return true
          return new Date(endDate) > new Date(startDate)
        }
      ),
    status: yup.boolean(),
  })

  const handleCompanyChange = async companyId => {
    formik.setFieldValue('companyId', companyId)
    formik.setFieldValue('branchOfficeIds', [])

    if (!companyId) {
      setBranchOffices([])
      return
    }
    await loadBranchOffices(companyId)
  }

  const loadBranchOffices = async companyId => {
    try {
      const payload = {
        CompanyId: companyId,
        PageSize: 100,
        PageNumber: 1,
      }
      const branchesInfo = await dispatch(getWharehouseInfo(payload))
      const branches = branchesInfo
        .map(branch => {
          return { ...branch.Warehouses[0] }
        })
        .filter(warehouse => {
          return warehouse.Description !== undefined
        })
      setBranchOffices(branches)
    } catch (error) {
      setBranchOffices([])
    }
  }

  const handleFileSelected = async event => {
    const file = event.target.files?.[0]
    if (!file) return

    const maxSizeMb = getMaxFileSize(formik.values.mediaType)
    const maxSizeBytes = maxSizeMb * 1024 * 1024

    if (file.size > maxSizeBytes) {
      setSnackbarMessage({
        open: true,
        message: `${t(
          'CUSTOMER_DISPLAY_SETTINGS.FORM.MAX_SIZE'
        )}: ${maxSizeMb} MB`,
        severity: 'error',
      })
      return
    }

    setIsUploading(true)
    const formData = new FormData()
    formData.append('FormFile', file)
    formData.append('TenantId', tenant)
    formData.append('EntityType', formik.values.mediaType)
    formData.append('EntityId', '')
    formData.append('InternalCode', '')
    formData.append('MediaType', formik.values.mediaType)
    formData.append('Date', new Date().toISOString())

    try {
      const resultFile = await dispatch(
        uploadFileAction(formData, {
          onProgress: percent => setUploadProgress(percent),
        })
      )
      const resultUrl = resultFile?.Data?.[0]?.TableDoc?.FileUrl

      if (!resultUrl) {
        throw new Error('File upload did not return a URL')
      }

      setFileName(file.name)
      setFileUrl(resultUrl)
    } catch (error) {
      setFileName('')
      setFileUrl('')
      event.target.value = ''
      setSnackbarMessage({
        open: true,
        message: t('CUSTOMER_DISPLAY_SETTINGS.FORM.FILE_UPLOAD_ERROR'),
        severity: 'error',
      })
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  const handleRemoveFile = () => {
    setFileName('')
    setFileUrl('')
  }

  const handleClose = () =>
    setSnackbarMessage({
      open: false,
      message: '',
      severity: 'warning',
    })

  const formik = useFormik({
    initialValues: {
      name: '',
      mediaType: 'VIDEO',
      displayOrder: '',
      companyId: '',
      branchOfficeIds: [],
      startDate: '',
      endDate: '',
      status: true,
    },
    validationSchema,
    onSubmit: async values => {
      if (!fileUrl) {
        setSnackbarMessage({
          open: true,
          message: t('CUSTOMER_DISPLAY_SETTINGS.FORM.FILE_REQUIRED'),
          severity: 'error',
        })
        return
      }

      setIsSubmitting(true)

      try {
        const payload = buildCustomerDisplayPayload({
          values,
          fileUrl,
          editMode,
          customerDisplaySettingId: id,
        })

        let success
        if (editMode) {
          success = await dispatch(modifyCustomerDisplaySetting(payload))
        } else {
          success = await dispatch(addCustomerDisplaySetting(payload))
        }

        if (success) {
          setSnackbarMessage({
            open: true,
            message: editMode
              ? t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.SUCCESS_EDIT')
              : t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.SUCCESS_SAVE'),
            severity: 'success',
          })

          history.push('/settings/customer-display')
        } else {
          setSnackbarMessage({
            open: true,
            message: editMode
              ? t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.FAIL_EDIT')
              : t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.FAIL_SAVE'),
            severity: 'error',
          })
        }
      } catch (error) {
        setSnackbarMessage({
          open: true,
          message: editMode
            ? t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.FAIL_EDIT')
            : t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.FAIL_SAVE'),
          severity: 'error',
        })
      } finally {
        setIsSubmitting(false)
      }
    },
  })

  const searchCompanies = async () => {
    try {
      const data = await dispatch(fetchCompanies())
      setCompanies(data)
    } catch (error) {
      setCompanies([])
    }
  }

  useEffect(() => {
    searchCompanies().then()
  }, [])

  useEffect(() => {
    if (!editMode) return

    const loadItem = async () => {
      setIsLoading(true)

      try {
        const item = await dispatch(retrieveCustomerDisplaySetting(id))

        if (!item?.FileUrl) return

        const companyId = (item.CompanyId || '').toUpperCase()

        const branchOfficeIds = (item.BranchOffices || [])
          .map(branchOffice => branchOffice.BranchOfficeId)
          .filter(Boolean)

        await formik.setValues({
          name: item.Name || '',
          mediaType: item.MediaType?.toUpperCase() || 'IMAGE',
          displayOrder: item.DisplayOrder ?? '',
          companyId,
          branchOfficeIds,
          startDate: toDateInputValue(item.StartDate),
          endDate: toDateInputValue(item.EndDate),
          status: !!item.Status,
        })

        setFileUrl(item.FileUrl || '')
        setFileName(item.FileUrl?.split('/').pop() || '')

        if (companyId) await loadBranchOffices(companyId)
      } catch (error) {
        setSnackbarMessage({
          open: true,
          message: t('CUSTOMER_DISPLAY_SETTINGS.MESSAGE.FAIL_LOAD'),
          severity: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadItem().then()
  }, [editMode, id])

  if (isLoading) {
    return <MiniLoading />
  }

  return (
    <>
      <div className="customer-display-information">
        <div className="customer-display-information-card">
          <Row>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="name">
                  {t('CUSTOMER_DISPLAY_SETTINGS.FORM.NAME')}
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="input-name"
                  className={
                    formik.touched.name && formik.errors.name
                      ? 'form-control error'
                      : 'form-control'
                  }
                  maxLength={200}
                  placeholder={t(
                    'CUSTOMER_DISPLAY_SETTINGS.FORM.PLACEHOLDER_NAME'
                  )}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.name && formik.errors.name && (
                <div className="input-feedback">
                  <strong data-testid="error-first-name">
                    {formik.errors.name}
                  </strong>
                </div>
              )}
            </Col>
            <Col md={12} lg={6}>
              <div className="form-group">
                <div className="field-details-container">
                  <label htmlFor="displayOrder">
                    {t('CUSTOMER_DISPLAY_SETTINGS.FORM.DISPLAY_ORDER')}
                  </label>
                  <ToolTipInformation
                    message={
                      <p style={{ fontSize: '12px' }}>
                        {t('CUSTOMER_DISPLAY_SETTINGS.FORM.FEED_DISPLAY_ORDER')}
                      </p>
                    }
                  />
                </div>
                <input
                  type="number"
                  id="displayOrder"
                  name="displayOrder"
                  data-testid="input-display-order"
                  className="form-control"
                  placeholder={t(
                    'CUSTOMER_DISPLAY_SETTINGS.FORM.PLACEHOLDER_DISPLAY_ORDER'
                  )}
                  value={formik.values.displayOrder}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="select-media-type">
                  {t('CUSTOMER_DISPLAY_SETTINGS.FORM.MEDIA_TYPE')}
                  <span className="required">*</span>
                </label>
                <FilterSelector
                  options={CUSTOMER_DISPLAY_MEDIA_TYPES}
                  getOptionValue={item => item.value}
                  getOptionLabel={item => t(item.label)}
                  value={formik.values.mediaType}
                  onChange={event => {
                    formik.setFieldValue('mediaType', event.value)
                    handleRemoveFile()
                  }}
                  testId="media-type"
                />
              </div>
              {formik.touched.mediaType && formik.errors.mediaType && (
                <div className="input-feedback">
                  <strong data-testid="error-media-type">
                    {formik.errors.mediaType}
                  </strong>
                </div>
              )}
            </Col>
            <Col md={12} lg={6}>
              <CustomerDisplayFileUpload
                fileName={fileName}
                fileUrl={fileUrl}
                accept={getAcceptedTypes(formik.values.mediaType)}
                maxSize={getMaxFileSize(formik.values.mediaType)}
                isUploading={isUploading}
                uploadProgress={uploadProgress}
                onFileSelected={handleFileSelected}
                onRemoveFile={handleRemoveFile}
              />
            </Col>
          </Row>
          {fileUrl && (
            <Row>
              <Col md={12}>
                <CustomerDisplayPreview
                  fileUrl={fileUrl}
                  mediaType={formik.values.mediaType}
                />
              </Col>
            </Row>
          )}
          <Row>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="select-company">
                  {t('CUSTOMER_DISPLAY_SETTINGS.FORM.COMPANY')}
                </label>
                <FilterSelector
                  options={companies}
                  getOptionValue={item => item.CompanyId}
                  getOptionLabel={item => item.Name}
                  value={formik.values.companyId}
                  onChange={event => handleCompanyChange(event.CompanyId)}
                  placeholder={t(
                    'CUSTOMER_DISPLAY_SETTINGS.FORM.PLACEHOLDER_COMPANY'
                  )}
                  testId="company"
                />
              </div>
            </Col>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="select-branch-office-ids">
                  {t('CUSTOMER_DISPLAY_SETTINGS.FORM.BRANCH_OFFICES')}
                  <span className="required">*</span>
                </label>
                <CustomerDisplayBranchSelector
                  id="branch-office-ids"
                  value={formik.values.branchOfficeIds}
                  options={branchOffices}
                  disabled={!formik.values.companyId}
                  onChange={branchOfficeIds =>
                    formik.setFieldValue('branchOfficeIds', branchOfficeIds)
                  }
                />
              </div>
              {formik.touched.branchOfficeIds &&
                formik.errors.branchOfficeIds && (
                  <div className="input-feedback">
                    <strong data-testid="error-branch-office-ids">
                      {formik.errors.branchOfficeIds}
                    </strong>
                  </div>
                )}
            </Col>
          </Row>

          <Row>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="start-date">
                  {t('CUSTOMER_DISPLAY_SETTINGS.FORM.START_DATE')}
                  <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="start-date"
                  name="startDate"
                  data-testid="input-start-date"
                  className={
                    formik.touched.startDate && formik.errors.startDate
                      ? 'form-control error'
                      : 'form-control'
                  }
                  value={formik.values.startDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.startDate && formik.errors.startDate && (
                <div className="input-feedback">
                  <strong data-testid="error-start-date">
                    {formik.errors.startDate}
                  </strong>
                </div>
              )}
            </Col>
            <Col md={12} lg={6}>
              <div className="form-group">
                <label htmlFor="end-date">
                  {t('CUSTOMER_DISPLAY_SETTINGS.FORM.END_DATE')}
                  <span className="required">*</span>
                </label>
                <input
                  type="date"
                  id="end-date"
                  name="endDate"
                  data-testid="input-end-date"
                  className={
                    formik.touched.endDate && formik.errors.endDate
                      ? 'form-control error'
                      : 'form-control'
                  }
                  value={formik.values.endDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </div>
              {formik.touched.endDate && formik.errors.endDate && (
                <div className="input-feedback">
                  <strong data-testid="error-end-date">
                    {formik.errors.endDate}
                  </strong>
                </div>
              )}
            </Col>
          </Row>
          {editMode && (
            <Row>
              <Col md={12} lg={6}>
                <div className="form-group">
                  <label htmlFor="switch-media-status">
                    {t('CUSTOMER_DISPLAY_SETTINGS.FORM.STATUS')}
                  </label>
                  <div className="toggle">
                    <label className="switch">
                      <input
                        id="switch-media-status"
                        type="checkbox"
                        checked={!!formik.values.status}
                        name="switch-media-status"
                        value={formik.values.status}
                        onChange={() =>
                          formik.setFieldValue('status', !formik.values.status)
                        }
                        data-testid="switch-media-status"
                      />
                      <span className="slider round"></span>
                    </label>
                    <p className="switch-label">
                      {formik.values.status
                        ? t('CUSTOMER_DISPLAY_SETTINGS.FORM.ACTIVE')
                        : t('CUSTOMER_DISPLAY_SETTINGS.FORM.INACTIVE')}
                    </p>
                  </div>
                </div>
              </Col>
            </Row>
          )}

          <div className="customer-display-information-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={() => history.push('/settings/customer-display')}
              disabled={isSubmitting}>
              {t('CUSTOMER_DISPLAY_SETTINGS.FORM.CANCEL')}
            </button>

            <button
              type="button"
              className="btn-save"
              disabled={isSubmitting || isUploading}
              onClick={formik.handleSubmit}>
              {isSubmitting ? (
                <CircularProgress size="2.5rem" />
              ) : !editMode ? (
                t('CUSTOMER_DISPLAY_SETTINGS.FORM.SAVE')
              ) : (
                t('CUSTOMER_DISPLAY_SETTINGS.FORM.UPDATE')
              )}
            </button>
          </div>
        </div>
      </div>
      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleClose}
      />
    </>
  )
}

export default CustomerDisplayInformation
